import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import { useNotification } from '../../../../../shared/contexts/useNotification';
import { AccountAction, AccountActionLink } from '../../../domain/models/AccountStatus';
import { AccountStatusIcon } from './components/AccountStatusIcon';
import { AccountActionButtons } from './components/AccountActionButtons';
import { useAccounts, useAccountAction } from '../../../application/hooks/useAccounts';
import { useAdminPreferences } from '../../../application/hooks/useAdminPreferences';
import { formatDate } from '../../../../../shared/utils/dateUtils';
import { DataTable } from '../../../../../shared/components/ui/DataTable';

/**
 * AccountListPage Component
 * Displays a list of all accounts in a data grid
 */
export const AccountListPage: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { data: accounts = [], isLoading: loading, error: queryError } = useAccounts();
  const accountAction = useAccountAction();
  const { language, timezone } = useAdminPreferences();

  const error =
    queryError instanceof Error ? queryError.message : queryError ? String(queryError) : null;

  // Handle account actions
  const handleAccountAction = async (
    actionLink: AccountActionLink,
    actionType: AccountAction,
  ): Promise<void> => {
    const successMessages: Record<AccountAction, string> = {
      remove: 'Account removed successfully',
      verify: 'Account verified successfully',
      cancel: 'Account cancelled successfully',
      activate: 'Account activated successfully',
    };
    const errorMessages: Record<AccountAction, string> = {
      remove: 'An error occurred trying to remove the account',
      verify: 'An error occurred trying to verify the account',
      cancel: 'An error occurred trying to cancel the account',
      activate: 'An error occurred trying to activate the account',
    };

    accountAction.mutate(actionLink, {
      onSuccess: () => showNotification(successMessages[actionType], 'success'),
      onError: () => showNotification(errorMessages[actionType], 'error'),
    });
  };

  const handleNewAccount = () => {
    navigate('/accounts/new');
  };

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/accounts/${params.row.id}`);
  };

  // Define columns for DataTable
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <AccountStatusIcon status={params.row.status} />,
    },
    {
      field: 'createdAt',
      headerName: 'Created at',
      width: 140,
      renderCell: (params) =>
        formatDate(params.row.createdAt, { language, timezone, includeTime: true }),
    },
    {
      field: 'updatedAt',
      headerName: 'Modified at',
      width: 140,
      renderCell: (params) =>
        formatDate(params.row.updatedAt, { language, timezone, includeTime: true }),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <AccountActionButtons
          links={params.row.links}
          accountName={params.row.name}
          onAction={(link, type) => handleAccountAction(link, type)}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '32px',
            letterSpacing: '-0.01em',
            color: '#0b1c30',
          }}
        >
          Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewAccount}
          sx={{ bgcolor: '#003ec7', '&:hover': { bgcolor: '#0031a0' }, boxShadow: 'none' }}
        >
          New Account
        </Button>
      </Box>

      <DataTable
        rows={accounts}
        columns={columns}
        loading={accountAction.isPending}
        emptyMessage="There are no accounts"
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
          columns: {
            columnVisibilityModel: { updatedAt: false },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
